import toastr from 'toastr';
import { v4 as uuidv4 } from 'uuid';
import { MFChannel } from '../../Components/SDK';
import { getActiveChannelId, getVirtualizedListRef } from '../../Slice';
import Store from '../../Store';
import { handleChannelIncomingMessage } from './channelEventHandler';
import { getLocalUserDetails } from './commonActions';
import { enqueueFile } from './mediaActions';

const scrollToBottom = (channelId, options = {}) => {
   const { delay = 500, maxRetries = 10, retryInterval = 200, messageType = 'text', smooth = false } = options;

   // Check if we have a virtualized list ref (for large lists)
   const virtualizedListRef = getVirtualizedListRef(channelId)();

   if (!virtualizedListRef?.jumpToBottom) return;

   const performScroll = (retryCount = 0) => {
      // For media messages, wait for images to load
      if (messageType === 'image' || messageType === 'video') {
         // Get the container element from the virtualized list ref's containerRef
         const container = virtualizedListRef.getScrollContainer?.();
         if (container) {
            const images = container.querySelectorAll('img');
            const unloadedImages = Array.from(images).filter(img => !img.complete || img.naturalHeight === 0);

            if (unloadedImages.length > 0 && retryCount < maxRetries) {
               setTimeout(() => performScroll(retryCount + 1), retryInterval);
               return;
            }
         }
      }

      // Use virtualized list's jumpToBottom
      virtualizedListRef.jumpToBottom({ smooth });
   };

   // Initial delay to allow DOM updates
   setTimeout(() => performScroll(), delay);
};

// Export a more robust scroll function for use in other components
export const scrollToBottomWithRetry = (scrollElement, options = {}) => {
   const { delay = 100, maxRetries = 10, retryInterval = 200, messageType = 'text', tolerance = 10 } = options;

   const performScroll = (retryCount = 0) => {
      if (!scrollElement) return;

      const currentScrollHeight = scrollElement.scrollHeight;
      const currentScrollTop = scrollElement.scrollTop;
      const clientHeight = scrollElement.clientHeight;

      // Always perform auto-scroll for channel messages (keep previous behavior)

      // For media messages, wait for images to load
      if (messageType === 'image' || messageType === 'video') {
         const images = scrollElement.querySelectorAll('img');
         const unloadedImages = Array.from(images).filter(img => !img.complete || img.naturalHeight === 0);

         if (unloadedImages.length > 0 && retryCount < maxRetries) {
            setTimeout(() => performScroll(retryCount + 1), retryInterval);
            return;
         }
      }

      // Perform the scroll
      requestAnimationFrame(() => {
         scrollElement.scrollTo({
            top: currentScrollHeight,
            behavior: 'smooth',
         });
      });
   };

   // Initial delay to allow DOM updates
   setTimeout(() => performScroll(), delay);
};

export const handleSendTextMessage = async (channelId, message) => {
   try {
      const messageObj = {
         messageId: `MSG-${uuidv4()}`,
         senderId: Store.getState().vCardData?.data?.userId || '',
         receiverId: '',
         channelId,
         chatType: 'channel',
         messageType: 'text',
         content: { message },
         replyTo: '',
         messageTime: Date.now(),
         createdAt: new Date().toISOString(),
         editedAt: null,
         updatedAt: new Date().toISOString(),
         deletedAt: null,
         meta: null,
      };
   // Add to Redux or store for UI visibility
   handleChannelIncomingMessage(messageObj);
   // For local sends we want to force-scroll to bottom so user sees the sent message
   scrollToBottom(channelId, { messageType: 'text', force: true });
      const response = await MFChannel.sendMessage(
         { channelId, content: message, messageId: messageObj.messageId },
         'text',
      );
      if (response.statusCode !== 200) {
         toastr.error(response.message);
      }
   } catch (error) {
      console.error('Error sending channel message:', error);
   }
};

export const handleSendMediaMessage = async (files = []) => {
   try {
      const channelId = getActiveChannelId();
      for (const file of files) {
         const {
            caption = '',
            mentionedUsersIds,
            name: fileName = '',
            size: fileSize = 0,
            fileDetails: { msgType, duration = 0, audioType = null } = {},
         } = file;
         let { fileDetails: { imageUrl } = {} } = file;
         imageUrl = URL.createObjectURL(file);
         const requiresDimension = ['image', 'video'].includes(msgType);
         const metadata = requiresDimension ? await MFChannel.getMediaMetadata({ file, mediaType: msgType }) : {};
         const {
            webWidth: _webWidth,
            webHeight: _webHeight,
            androidWidth,
            androidHeight,
            originalWidth,
            originalHeight,
         } = metadata;

         const messageId = `MSG-${uuidv4()}`;

         const fileDetails = {
            mentionedUsersIds,
            caption,
            fileName,
            duration,
            file_size: fileSize,
            file_url: imageUrl,
            webWidth: _webWidth || 0,
            webHeight: _webHeight || 0,
            androidWidth: androidWidth || 0,
            androidHeight: androidHeight || 0,
            originalWidth: originalWidth || 0,
            originalHeight: originalHeight || 0,
            audioType,
            thumb_image: metadata.base64 || '',
         };

         const messagePayload = {
            is_downloaded: 2,
            is_uploading: 0,
            thumb_image: metadata.base64 || '',
            local_path: imageUrl,
            ...fileDetails,
            file,
         };

         const fileOptionsForSDK = {
            fileDetails,
            messageId,
            channelId,
            file,
         };

         const messageData = {
            messageId,
            content: { media: messagePayload },
            messageType: msgType,
            channelId,
            chatType: 'channel',
            senderId: getLocalUserDetails().userId,
            messageTime: Date.now(),
         };
         // For local media sends we force scroll to bottom
         scrollToBottom(channelId, { messageType: msgType, force: true });

         handleChannelIncomingMessage(messageData);
         enqueueFile(messageData, channelId, fileOptionsForSDK, msgType);
      }
   } catch (error) {
      console.error('Failed to enqueue media files:', error);
   }
};

export const parseReactions = reactionsStr => {
   if (!reactionsStr) return {};

   try {
      return typeof reactionsStr === 'string' ? JSON.parse(reactionsStr) : reactionsStr;
   } catch (err) {
      console.error('Failed to parse reactions string', err);
      return {};
   }
};
